import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { MapPin, Phone, Mail, Globe, MessageSquare, Send } from 'lucide-react';
import SeoMeta from '../components/SeoMeta';
import StructuredData, { serviceSchema, faqSchema } from '../components/StructuredData';
import '../styles/StudyVisaPage.css';

const StudyVisaPage = () => {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    subject: '',
    message: ''
  });

  const features = [
  "Global Presence with Local Expertise (Headquartered in Singapore, branches across Asia)",
  "High Visa Success Rate with Proven Student Placements",
  "End-to-End Support from Application to Arrival",
  "Expert Guidance in University Selection",
  "Accommodation & Travel Assistance",
  "Career-Friendly Study Options with Part-Time Work Opportunities"
];

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleInputChange = handleFormChange;

  const handleWhatsAppSubmit = (e) => {
    e.preventDefault();
    
    const { fullName, email, subject, message } = formData;
    
    // Validate form
    if (!fullName || !email || !subject || !message) {
      alert('Please fill all fields');
      return;
    }

    // Create WhatsApp message
    const whatsappMessage = `Hello Bright Angel,

    *New Student Visa Inquiry*

    *Applicant Details:*
    Name: ${fullName}
    Email: ${email}

    *Inquiry Information:*
    Subject: ${subject}
    Message: ${message}

    I am interested in obtaining guidance regarding student visa opportunities. Kindly review my inquiry and get back to me at your earliest convenience with the relevant details.

    Thank you.`;

    // Encode message for WhatsApp
    const encodedMessage = encodeURIComponent(whatsappMessage);
    
    // WhatsApp number (Singapore HQ)
    const whatsappNumber = '+94774950499';
    
    // Open WhatsApp
    window.open(`https://wa.me/${whatsappNumber}?text=${encodedMessage}`, '_blank');
    
    // Reset form
    setFormData({
      fullName: '',
      email: '',
      subject: '',
      message: ''
    });
  };

  const handleSubmit = handleWhatsAppSubmit;

  // SEO: FAQ Schema for Study Visa
  const studyVisaFaqs = [
    {
      question: "What is a study visa?",
      answer: "A study visa is an official authorization from a country that allows international students to enter and remain for the purpose of pursuing educational programs. It is required by students from Sri Lanka seeking to study in countries like UK, Canada, Australia, Singapore, and Malaysia."
    },
    {
      question: "How long does study visa processing take?",
      answer: "Study visa processing times vary by country. UK study visas typically take 3-4 weeks, Canada 4-6 weeks, Australia 2-4 weeks, Singapore 2-3 weeks, and Malaysia 3-5 days. Bright Angel expedites the process with expert documentation support."
    },
    {
      question: "What documents are required for a study visa application?",
      answer: "Study visa applications generally require: passport, educational certificates, English proficiency scores (IELTS/TOEFL), university acceptance letter, proof of funds, sponsor documentation, bank statements, and a completed visa application form. Our experts guide you through each document."
    },
    {
      question: "What is the cost of study visa application?",
      answer: "Study visa costs depend on the destination country. Canada charges CAD 150, Australia AUD 630, UK £719, Singapore typically free with acceptance, and Malaysia has no study visa fee. Bright Angel consultants provide transparent fee information."
    }
  ];

  return (
    <>
      {/* SEO Meta Tags */}
      <SeoMeta
        title="Study Visa Consultant Sri Lanka | Bright Angel Expert Guidance UK, Canada, Australia"
        description="Bright Angel - Expert study visa consultancy in Sri Lanka. We provide comprehensive study visa guidance for UK, Canada, Australia, Singapore & Malaysia with proven success rates and trusted service."
        keywords="Bright Angel, study visa, Bright Angel study visa, study visa consultant, student visa Sri Lanka, study visa application, visa processing, international study, study abroad, visa consultant"
        url="https://brightangel.lk"
        image="https://brightangel.lk/og-image.png"
      />

      {/* Structured Data for Study Visa Service */}
      <StructuredData
        data={serviceSchema(
          'Bright Angel Study Visa Consultation Service',
          'Bright Angel expert study visa consultancy for Sri Lankan students. Comprehensive guidance for UK, Canada, Australia, Singapore, and Malaysia study visas with end-to-end support. Trust Bright Angel for visa processing.',
          'https://brightangel.lk/og-image.png',
          'Contact Bright Angel for pricing'
        )}
        type="service-schema"
      />

      {/* FAQ Schema */}
      <StructuredData
        data={faqSchema(studyVisaFaqs)}
        type="faq-schema"
      />
    
    <div className="study-visa-page">
      {/* Hero Section */}
      <section className="hero-section container">
        <div className="hero-content">
          <div className="badge-light mb-4">Bright Angel Study Visa Consultancy</div>
          <h1 className="hero-title">
            Get Your <span className="text-brand-blue">Study Visa</span><br/>
            <span className="text-brand-green">Easy & Fast with Bright Angel!</span>
          </h1>
          <p className="hero-subtitle"><strong>Bright Angel</strong> is the best study visa consultant in Sri Lanka. Expert guidance for study visa applications to UK, Canada, Australia, Singapore & Malaysia. We provide reliable, transparent, and efficient study visa pathways for international students.</p>
          
          <div className="hero-mission-vision">
            <div className="mission-card">
              <h4>Our Mission</h4>
              <p>We are dedicated to transforming the educational experience by bridging the gap between talented students and prestigious global universities. We don't just process visas - we build futures, empower dreams, and fuel aspirations.</p>
            </div>
            
            <div className="mission-card">
              <h4>Our Vision</h4>
              <p>Our vision is to redefine education consulting through cutting-edge technology, professional expertise, and deep market insight. We strive to become the world's most trusted, innovative, and impact-driven education partner.</p>
            </div>
          </div>

          <div className="hero-actions">
            <a href="#destinations" className="btn-primary">Explore Destinations &rarr;</a>
            <a href="#about" className="btn-link">Why Choose Us?</a>
          </div>
        </div>
      </section>

      {/* Features & Images Section */}
      <section id="about" className="features-section">
        <div className="container about-container">
          
          <div className="about-content">
            <h2 className="section-title-left">Why Choose Bright Angel for Study Visa Consulting?</h2>
            <p className="about-description">
              <strong>Bright Angel</strong> provides comprehensive study visa guidance and consulting to make your international education dreams a reality with zero hassle. Our expert team specializes in study visa applications for all major destinations.
            </p>
            <ul className="features-list">
              {features.map((feature, index) => (
                <li key={index} className="feature-item">
                  <span className="feature-icon">✓</span>
                  <span>{feature}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="about-images">
            <img 
              src="https://images.unsplash.com/photo-1523240795612-9a054b0db644?w=800&h=600&fit=crop" 
              alt="Group of diverse students laughing on campus" 
              className="student-img-main"
            />
            <div className="student-img-card">
              <div className="img-badge">Trusted by 1000+ Students</div>
            </div>
          </div>

        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="services-section">
        <div className="container">
          <div className="services-header-group">
            <span className="services-badge">Bright Angel Study Visa & Admission Services</span>
            <h2 className="services-title">Bright Angel: Complete Study Visa Solutions for International Students</h2>
            <p className="services-subtitle">Expert Bright Angel consultancy - end-to-end study visa support for your international education journey</p>
          </div>

          <div className="services-grid">
            <div className="service-card-modern">
              <div className="service-card-content">
                <h3>Study Visa Expert Consultation</h3>
                <p className="service-description">Personalized study visa guidance from certified experts</p>
                <ul className="service-features">
                  <li>Career pathway assessment</li>
                  <li>Study visa requirement analysis</li>
                  <li>University selection guidance</li>
                  <li>Application support</li>
                  <li>Scholarship guidance</li>
                </ul>
              </div>
            </div>

            <div className="service-card-modern">
              <div className="service-card-content">
                <h3>Study Visa Processing & Documentation</h3>
                <p className="service-description">Complete study visa documentation & embassy support</p>
                <ul className="service-features">
                  <li>Study visa document preparation</li>
                  <li>Mock interview training</li>
                  <li>Embassy coordination</li>
                  <li>Visa application filing</li>
                  <li>Study visa status tracking</li>
                </ul>
              </div>
            </div>

            <div className="service-card-modern">
              
              <div className="service-card-content">
                <h3>Language Preparation</h3>
                <p className="service-description">Exam coaching for IELTS, TOEFL, JLPT, NAT</p>
                <ul className="service-features">
                  <li>Professional coaching classes</li>
                  <li>Practice materials & mock tests</li>
                  <li>One-on-one mentoring</li>
                  <li>Exam registration support</li>
                  <li>Score improvement guarantee</li>
                </ul>
              </div>
            </div>

            <div className="service-card-modern">
              
              <div className="service-card-content">
                <h3>Financial Services</h3>
                <p className="service-description">Funding solutions & financial planning</p>
                <ul className="service-features">
                  <li>Educational loan partnerships</li>
                  <li>Scholarship opportunities</li>
                  <li>Financial planning</li>
                  <li>Sponsor documentation</li>
                  <li>Bank statement assistance</li>
                </ul>
              </div>
            </div>

            <div className="service-card-modern">
              
              <div className="service-card-content">
                <h3>Pre-Departure Support</h3>
                <p className="service-description">Complete preparation for your journey abroad</p>
                <ul className="service-features">
                  <li>Flight & accommodation booking</li>
                  <li>Travel insurance guidance</li>
                  <li>Pre-departure orientation</li>
                  <li>Cultural awareness training</li>
                  <li>Medical documentation</li>
                </ul>
              </div>
            </div>

            <div className="service-card-modern">
              
              <div className="service-card-content">
                <h3>Post-Arrival Support</h3>
                <p className="service-description">Ongoing assistance after you arrive</p>
                <ul className="service-features">
                  <li>Airport pickup & transfers</li>
                  <li>Accommodation assistance</li>
                  <li>University enrollment support</li>
                  <li>Job placement guidance</li>
                  <li> 24/7 emergency support</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        
      </section>

      {/* Destinations Section */}
      <section id="destinations" className="destinations-section container">
        <h2 className="section-title">Popular Study Visa <span className="text-brand-blue">Destinations</span></h2>
        
        <div className="destinations-grid">
          <div className="dest-card">
            <img src="https://images.pexels.com/photos/460672/pexels-photo-460672.jpeg?auto=compress&cs=tinysrgb&w=600" alt="UK study visa guide" className="dest-img"/>
            <div className="dest-info">
              <span className="dest-badge">Popular</span>
              <h3>United Kingdom Study Visa</h3>
              <p>Expert study visa guidance & Top Ranked Universities</p>
            </div>
          </div>
          
          <div className="dest-card">
            <img src="https://images.unsplash.com/photo-1517935706615-2717063c2225?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80" alt="Canada study visa processing" className="dest-img"/>
            <div className="dest-info">
              <span className="dest-badge">High Demand</span>
              <h3>Canada Study Visa</h3>
              <p>Excellent Permanent Residency after Study Visa</p>
            </div>
          </div>

          <div className="dest-card">
            <img src="https://images.unsplash.com/photo-1523482580672-f109ba8cb9be?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80" alt="Australia student visa" className="dest-img"/>
            <div className="dest-info">
              <span className="dest-badge">Post-Study Work</span>
              <h3>Australia Study Visa</h3>
              <p>High standard living & Study Visa Success Rate</p>
            </div>
          </div>
          
          <div className="dest-card">
            <img src="https://images.unsplash.com/photo-1525625293386-3f8f99389edd?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80" alt="Singapore education visa" className="dest-img"/>
            <div className="dest-info">
              <span className="dest-badge text-brand-green" style={{background: 'var(--color-secondary-light-green)'}}>No IELTS</span>
              <h3>Singapore Study Visa</h3>
              <p>Affordable Education Visa with Global Recognition</p>
            </div>
          </div>
          
          <div className="dest-card">
            <img src="https://images.pexels.com/photos/3385750/pexels-photo-3385750.jpeg?auto=compress&cs=tinysrgb&w=600" alt="Malaysia student visa fast track" className="dest-img"/>
            <div className="dest-info">
              <span className="dest-badge text-brand-green" style={{background: 'var(--color-secondary-light-green)'}}>Fast Process</span>
              <h3>Malaysia Study Visa</h3>
              <p>Fastest Study Visa Processing - Only 3 Days</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section before Contact */}
      <section className="cta-ready-section">
        <div className="container">
          <h2 className="cta-ready-title">Ready to Partner with Excellence?</h2>
          <p className="cta-ready-subtitle">Explore our diverse business opportunities and learn more about our services, or discuss potential collaborations across our diversified business verticals.</p>
          <div className="cta-ready-buttons">
            <a href="#contact" className="btn-primary">Contact Us Today</a>
            <a href="tel:+94774950499" className="btn-secondary" style={{ backgroundColor: 'transparent', color: 'white', borderColor: 'white' }}>Call +94 77 495 0499</a>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="contact-section">
        <div className="contact-header-main">
            <h2 className="contact-main-title">Get in Touch with <span>Bright Angel</span></h2>
            <p className="contact-main-subtitle">Reach out to Bright Angel team for study visa inquiries and support</p>
          </div>
        <div className="container contact-container">
          <div className="contact-grid">
                    <motion.div
                        className="contact-info-column"
                        initial={{ opacity: 0, x: -30 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                    >
                        <div className="info-header">
                            
                            <h2>BRIGHT ANGEL</h2>
                            <p className="info-description">
                                Visit us at our branch or reach out through any of the following channels.
                            </p>
                        </div>

                        <div className="contact-item">
                            <div className="item-icon">
                                <MapPin size={28} />
                            </div>
                            <div className="item-text">
                                <h4>Our Location</h4>
                                <p>48/3/1, Galle Road, Dehiwala</p>
                            </div>
                        </div>

                        <div className="contact-item">
                            <div className="item-icon">
                                <Phone size={28} />
                            </div>
                            <div className="item-text">
                                <h4>Phone Number</h4>
                                <p>+94 77 495 0499</p>
                            </div>
                        </div>

                        <div className="contact-item">
                            <div className="item-icon">
                                <Mail size={28} />
                            </div>
                            <div className="item-text">
                                <h4>Email Address</h4>
                                <p>studyvisa@brightangel.com</p>
                            </div>
                        </div>

                        <div style={{ marginTop: '1rem', display: 'flex', gap: '1rem' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: 'var(--text-light)', fontSize: '0.9rem' }}>
                                <Globe size={16} />
                                <span>Global Reach</span>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: 'var(--text-light)', fontSize: '0.9rem' }}>
                                <MessageSquare size={16} />
                                <span>24/7 Support</span>
                            </div>
                        </div>
                    </motion.div>

                    <motion.div
                        className="contact-form-column"
                        initial={{ opacity: 0, x: 30 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6, delay: 0.2 }}
                    >
                        <form onSubmit={handleSubmit}>
                            <div className="form-group">
                                <label className="form-label">Full Name</label>
                                <input
                                    type="text"
                                    name="fullName"
                                    className="form-input"
                                    placeholder="Enter your full name"
                                    value={formData.fullName}
                                    onChange={handleInputChange}
                                    required
                                />
                            </div>
                            <div className="form-group">
                                <label className="form-label">Email Address</label>
                                <input
                                    type="email"
                                    name="email"
                                    className="form-input"
                                    placeholder="your@email.com"
                                    value={formData.email}
                                    onChange={handleInputChange}
                                    required
                                />
                            </div>
                            <div className="form-group">
                                <label className="form-label">Subject</label>
                                <input
                                    type="text"
                                    name="subject"
                                    className="form-input"
                                    placeholder="What is this regarding?"
                                    value={formData.subject}
                                    onChange={handleInputChange}
                                    required
                                />
                            </div>
                            <div className="form-group">
                                <label className="form-label">Your Message</label>
                                <textarea
                                    name="message"
                                    className="form-input"
                                    rows="5"
                                    placeholder="How can we help you today?"
                                    value={formData.message}
                                    onChange={handleInputChange}
                                    required
                                    style={{ resize: 'none' }}
                                ></textarea>
                            </div>
                            <button type="submit" className="submit-btn">
                                <Send size={20} /> Send Message via WhatsApp
                            </button>
                        </form>
                    </motion.div>
                </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="map-section">
        <div className="container">
          <h2 className="section-title" style={{marginBottom: '3rem'}}>Our Global Branches</h2>
          
          <div className="map-branches-wrapper">
            <div className="map-container">
              <iframe 
                src="https://maps.google.com/maps?q=48/3/1,%20Galle%20Road,%20Dehiwala,%20Sri%20Lanka&t=&z=13&ie=UTF8&iwloc=&output=embed" 
                width="100%" 
                height="100%" 
                style={{border: 0}} 
                allowFullScreen="" 
                loading="lazy" 
                referrerPolicy="no-referrer-when-downgrade"
                title="Bright Angel Location Map"
              ></iframe>
            </div>

            <div className="branches-list">
              <div className="branch-item">
                <h4 className="branch-name">Singapore HQ</h4>
                <p className="branch-detail">📍 205, Balestier Road, 01-04, The Mezzo, 329682</p>
                <p className="branch-detail">📞 +65 6025 4431 / 4432</p>
                <p className="branch-detail">✉️ admin@brightangelgroup.com</p>
              </div>

              <div className="branch-item">
                <h4 className="branch-name">India Branch</h4>
                <p className="branch-detail">📍 174/B1, Dharmapuri - Tirupattur Main Road, Pochampalli, 635206</p>
                <p className="branch-detail">📞 04341 203365</p>
                <p className="branch-detail">✉️ admin@brightangelgroup.com</p>
              </div>

              <div className="branch-item">
                <h4 className="branch-name">Malaysia Branch</h4>
                <p className="branch-detail">📍 699, Jalan Damansara, TTDI, Kuala Lumpur, Malaysia</p>
                <p className="branch-detail">📞 +65 6025 4431</p>
                <p className="branch-detail">✉️ admin@brightangelgroup.com</p>
              </div>

              <div className="branch-item">
                <h4 className="branch-name">Sri Lanka Branch</h4>
                <p className="branch-detail">📍 No: 48, 3/1, Galle Road, Dehiwala, Sri Lanka</p>
                <p className="branch-detail">📞 +94 114904530</p>
                <p className="branch-detail">✉️ info@brightangel.lk</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      
    </div>
    </>
  );
};

export default StudyVisaPage;
